/**
 * @date: 2023-04-19
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useCallback, useState} from 'react';
import Typography from "@mui/material/Typography";
import {COLORS} from "./BasicPie";
import {Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip} from "recharts";

const RADIAN = Math.PI / 180;




export default function CurrentSyncPie(props) {
    const {
        others,
        data
    } = props;
    console.log('[CurrentSyncPie] data prop:', data);
    console.log('[CurrentSyncPie] others prop:', others);

    const [activeIndex, setActiveIndex] = useState(null);
    const onPieEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, [setActiveIndex])
    const onPieLeave = useCallback((_, index) => {
        setActiveIndex(null);
    }, [])

    const renderActiveShape = props => {
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            midAngle,
            fill
        } = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const HOVER_OFFSET = 8;
        const sx = cx + HOVER_OFFSET * cos;
        const sy = cy + HOVER_OFFSET * sin;
        return (
            <Sector
                cx={sx}
                cy={sy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                style={{ filter: `drop-shadow(0px 0px 5px ${fill}`}}
            />
        );
    };

    const customizeTooltip = ({payload, active, label}) => {
        if (active) {
            const name = payload[0].name;
            const value = payload[0].value;
            if (name !== 'others') {
                return (
                    <div style={{
                        backgroundColor: "#b3e5fc",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        boxShadow: "0 1px 8px rgba(0,0,0,0.5"
                    }}>
                        <span>{`${name}(${value})`}</span>
                    </div>
                );
            } else {
                return (
                    <div style={{
                        backgroundColor: "#b3e5fc",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        boxShadow: "0 1px 8px rgba(0,0,0,0.5",
                        fontSize: '12px'
                    }}>
                        <b>Others:</b>
                        {others && others.map( (d) => (
                            <div key={d.name}>
                                <span>{`${d.name}(${d.value})`}</span>
                                <br/>
                            </div>
                        ))}
                    </div>
                );
            }}
        return null;
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, fill }) => {
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 30;
        const ey = my;
        const textAnchor = cos >= 0 ? "start" : "end";

        return (
            <g>
                <path
                    d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                    stroke={fill}
                    fill="none"
                />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey + 5} // adjust the text vertically
                    textAnchor={textAnchor}
                    fill="#777777"
                >
                    {data[index].name}({`${(percent * 100).toFixed(2)}%`})
                </text>
            </g>
        );
    };


    const hasData = data && data.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <Typography fontWeight='bold' color={'#777'} marginBottom={3}>Percent of FHIR Endpoint Resources</Typography>
            {!hasData ? (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color={'#aaa'} fontSize={'0.9rem'}>
                        No per-HISP endpoint data available
                    </Typography>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart margin={{ top: 40, right: 80, bottom: 40, left: 80 }}>
                        <Pie
                            data={data}
                            label={renderCustomizedLabel}
                            outerRadius="68%"
                            fill="#8884d8"
                            dataKey="value"
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            onMouseEnter={onPieEnter}
                            onMouseLeave={onPieLeave}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={customizeTooltip}/>
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
    )

}

